import type { FC, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

export interface DetailGridItem {
  label: string
  value: ReactNode
  /** Render the value in the theme's monospace stack (IDs, codes, dates). */
  mono?: boolean
}

interface DetailGridProps {
  items: DetailGridItem[]
  columns?: 1 | 2 | 3
}

/**
 * Label/value pair grid for detail sections (prior-auth details, insurance
 * coverage, device info). Rendered as a definition list so each label is
 * programmatically associated with its value.
 */
export const DetailGrid: FC<DetailGridProps> = ({ items, columns = 2 }) => (
  <Box
    component="dl"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, 1fr)` },
      gap: 1.5,
      m: 0,
    }}
  >
    {items.map(item => (
      <Box key={item.label} sx={{ minWidth: 0 }}>
        <Typography
          component="dt"
          sx={{
            color: 'text.secondary',
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {item.label}
        </Typography>
        <Typography
          component="dd"
          sx={theme => ({
            m: 0,
            mt: 0.25,
            color: 'text.primary',
            fontSize: '1.3rem',
            overflowWrap: 'anywhere',
            ...(item.mono
              ? { fontFamily: theme.typography.fontFamilyMono, fontSize: '1.25rem' }
              : {}),
          })}
        >
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
)
